import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Radio,
  Stack,
  Stepper,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconDownload,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { correctionsApi } from "@services/correctionsApi";
import { triggerDownload } from "@utils/download";
import type {
  CorrectionState,
  CorrectionView,
  ProblemEntry,
  RecentCorrection,
  ResultRow,
} from "@models/corrections";
import { ReviewFileDrop } from "./ReviewFileDrop";
import correctionExample from "@assets/correction-example.png";

const MAX_UPLOAD_SIZE = 100 * 1024 * 1024;
const POLL_MS = 2000;
/** UI hint only: the lookup / apply normally take seconds. */
const SLOW_AFTER_MS = 5 * 60 * 1000;
const PAGE_SIZE = 25;
const OPEN_STATES: CorrectionState[] = ["looking_up", "ready", "applying"];
const POLL_STATES: CorrectionState[] = ["looking_up", "applying"];
const SKIP = "skip";

const storageKey = (kpi: string) => `dqm.correction.${kpi}`;

/**
 * "Correct verdicts" tab (PBI #747204): one guided flow per browser session —
 * ① Upload → ② Check (only with problem rows) → ③ Review → ④ Done.
 *
 * The correction id lives in sessionStorage, so a reload continues the same correction but a
 * closed tab doesn't. All state is on the server (events.json); this component only renders
 * the step that state implies and polls while a Lambda is working.
 */
export const CorrectVerdicts: React.FC<{ kpi: string }> = ({ kpi }) => {
  const { t } = useTranslation();
  const [correctionId, setCorrectionId] = useState<string | null>(
    () => sessionStorage.getItem(storageKey(kpi)),
  );
  const [view, setView] = useState<CorrectionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkDone, setCheckDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const remember = useCallback(
    (id: string | null) => {
      if (id) sessionStorage.setItem(storageKey(kpi), id);
      else sessionStorage.removeItem(storageKey(kpi));
      setCorrectionId(id);
    },
    [kpi],
  );

  const reload = useCallback(async () => {
    if (!correctionId) return;
    try {
      const v = await correctionsApi.get(kpi, correctionId);
      if (v.state === "discarded") {
        remember(null);
        setView(null);
        return;
      }
      setView(v);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.error"));
    }
  }, [correctionId, kpi, remember, t]);

  useEffect(() => {
    setView(null);
    void reload();
  }, [reload]);

  // Poll while a Lambda is working.
  const state = view?.state;
  useEffect(() => {
    if (!state || !POLL_STATES.includes(state)) return;
    const timer = window.setInterval(() => void reload(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [state, reload]);

  // Leaving the page with an open correction discards it — warn first.
  useEffect(() => {
    if (!state || !OPEN_STATES.includes(state)) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [state]);

  const startOver = async () => {
    setError(null);
    if (correctionId && view && (view.state === "looking_up" || view.state === "ready") && view.is_owner) {
      try {
        await correctionsApi.discard(kpi, correctionId);
      } catch (e) {
        // The correction may have expired meanwhile; starting over is still right.
        console.warn("discard failed", e);
      }
    }
    remember(null);
    setView(null);
    setCheckDone(false);
  };

  const upload = async (file: File) => {
    setError(null);
    if (file.size > MAX_UPLOAD_SIZE) {
      setError(t("reviews.upload.fileTooLarge"));
      return;
    }
    if (!/\.(xlsx|csv)$/i.test(file.name)) {
      setError(t("reviews.correct.upload.wrongType"));
      return;
    }
    setBusy(true);
    try {
      if (correctionId) await startOver();
      const created = await correctionsApi.create(kpi, file.name);
      await correctionsApi.uploadFile(created.url, file);
      await correctionsApi.submit(kpi, created.correction_id, file.name);
      setCheckDone(false);
      remember(created.correction_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const problems = view?.problems ?? [];
  const step = !view
    ? 0
    : view.state === "looking_up" || view.state === "lookup_failed"
      ? 0
      : view.state === "ready"
        ? problems.length > 0 && !checkDone
          ? 1
          : 2
        : 3;

  return (
    <Paper withBorder p="lg">
      <Stack gap="lg">
        <Stepper active={step} size="sm" allowNextStepsSelect={false}>
          <Stepper.Step label={t("reviews.correct.steps.upload")} />
          <Stepper.Step label={t("reviews.correct.steps.check")} />
          <Stepper.Step label={t("reviews.correct.steps.review")} />
          <Stepper.Step label={t("reviews.correct.steps.done")} />
        </Stepper>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" withCloseButton onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {view?.state === "expired" ? (
          <Stack gap="md" align="flex-start">
            <Alert icon={<IconAlertCircle size={16} />} color="orange" data-testid="correction-expired">
              {t("reviews.correct.expired")}
            </Alert>
            <Button onClick={() => void startOver()}>{t("reviews.correct.startNew")}</Button>
          </Stack>
        ) : view?.state === "unknown" ? (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {t("reviews.correct.unknownState", { id: view.correction_id })}
          </Alert>
        ) : step === 0 ? (
          <UploadStep
            kpi={kpi}
            view={view}
            busy={busy}
            onFile={(f) => void upload(f)}
          />
        ) : step === 1 && view ? (
          <CheckStep
            view={view}
            onStartOver={() => void startOver()}
            onContinue={async (choices) => {
              try {
                await correctionsApi.saveChoices(kpi, view.correction_id, choices);
                await reload();
                setCheckDone(true);
              } catch (e) {
                setError(e instanceof Error ? e.message : t("common.error"));
              }
            }}
          />
        ) : step === 2 && view ? (
          <ReviewStep
            view={view}
            hasProblems={problems.length > 0}
            onBack={() => setCheckDone(false)}
            onError={setError}
            onDecided={() => void reload()}
          />
        ) : view ? (
          <DoneStep view={view} onError={setError} onStartNew={() => void startOver()} />
        ) : null}
      </Stack>
    </Paper>
  );
};

// ── ① Upload ────────────────────────────────────────────────────────────────

const UploadStep: React.FC<{
  kpi: string;
  view: CorrectionView | null;
  busy: boolean;
  onFile: (f: File) => void;
}> = ({ kpi, view, busy, onFile }) => {
  const { t } = useTranslation();
  const lookingUp = view?.state === "looking_up";
  const slow = lookingUp && Date.now() - Date.parse(view.last_activity) > SLOW_AFTER_MS;

  return (
    <Stack gap="md">
      <Text size="sm">{t("reviews.correct.upload.intro")}</Text>

      {view?.state === "lookup_failed" && view.failure && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" role="alert" data-testid="correction-rejected">
          {view.failure.message}
        </Alert>
      )}

      {lookingUp || busy ? (
        <Group gap="sm" data-testid="correction-looking-up">
          <Loader size="sm" />
          <Text size="sm">{t("reviews.correct.upload.lookingUp")}</Text>
        </Group>
      ) : (
        <ReviewFileDrop
          value={null}
          onChange={(f) => f && onFile(f)}
          error={null}
          accept=".xlsx,.csv"
          labels={{ title: t("reviews.correct.upload.dropTitle"), choose: t("reviews.correct.upload.choose") }}
        />
      )}
      {slow && (
        <Alert icon={<IconAlertTriangle size={16} />} color="orange">
          {t("reviews.correct.slow", { id: view.correction_id })}
        </Alert>
      )}

      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Stack gap="sm">
          <Text size="sm">{t("reviews.correct.upload.needed")}</Text>
          <Box>
            <Text size="xs" c="dimmed" mb={4}>
              {t("reviews.correct.upload.exampleCaption")}
            </Text>
            <img
              data-testid="correction-example-image"
              src={correctionExample}
              alt={t("reviews.correct.upload.exampleAlt")}
              style={{ maxWidth: 260, border: "1px solid var(--mantine-color-gray-3)", borderRadius: 4 }}
            />
          </Box>
        </Stack>
      </Alert>

      <RecentCorrections kpi={kpi} />
    </Stack>
  );
};

const RecentCorrections: React.FC<{ kpi: string }> = ({ kpi }) => {
  const { t } = useTranslation();
  const [recent, setRecent] = useState<RecentCorrection[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    correctionsApi
      .recent(kpi)
      .then((r) => setRecent(r.corrections))
      .catch((e) => setError(e instanceof Error ? e.message : t("common.error")));
  }, [kpi, t]);

  return (
    <Box data-testid="recent-corrections">
      <Text fw={600} size="sm" mb="xs">
        {t("reviews.correct.recent.title")}
      </Text>
      {error ? (
        <Text size="sm" c="red">{error}</Text>
      ) : recent === null ? (
        <Loader size="xs" />
      ) : recent.length === 0 ? (
        <Text size="sm" c="dimmed">{t("reviews.correct.recent.none")}</Text>
      ) : (
        <Stack gap={4}>
          {recent.map((r) => (
            <Group key={r.correction_id} gap="md">
              <Text size="sm" w={150}>{new Date(r.applied_at).toLocaleString()}</Text>
              <Text size="sm" w={200}>{r.uploaded_by}</Text>
              <Text size="sm" w={170}>{t("reviews.correct.recent.changed", { count: r.written })}</Text>
              <OverviewButton kpi={kpi} correctionId={r.correction_id} compact />
            </Group>
          ))}
          <Text size="xs" c="dimmed">{t("reviews.correct.recent.note")}</Text>
        </Stack>
      )}
    </Box>
  );
};

const OverviewButton: React.FC<{
  kpi: string;
  correctionId: string;
  compact?: boolean;
  onError?: (m: string) => void;
}> = ({ kpi, correctionId, compact, onError }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  return (
    <Button
      size="xs"
      variant={compact ? "subtle" : "light"}
      leftSection={<IconDownload size={14} />}
      loading={loading}
      onClick={async () => {
        setLoading(true);
        try {
          const { url, filename } = await correctionsApi.overview(kpi, correctionId);
          await triggerDownload(url, filename);
        } catch (e) {
          const message = e instanceof Error ? e.message : t("common.error");
          if (onError) onError(message);
          else console.error(message);
        } finally {
          setLoading(false);
        }
      }}
    >
      {t(compact ? "reviews.correct.overviewShort" : "reviews.correct.overview")}
    </Button>
  );
};

// ── ② Check ─────────────────────────────────────────────────────────────────

const verdictLabel = (t: (k: string) => string, v: 0 | 1 | null) =>
  v === null ? "—" : t(v === 1 ? "reviews.correct.verdict.correct" : "reviews.correct.verdict.incorrect");

const firstText = (c: { display: { header: string; ui: string }[] }) =>
  c.display.find((d) => d.header.toLowerCase().startsWith("text") && d.ui)?.ui ?? "";

const CheckStep: React.FC<{
  view: CorrectionView;
  onStartOver: () => void;
  onContinue: (choices: Record<string, string>) => Promise<void>;
}> = ({ view, onStartOver, onContinue }) => {
  const { t } = useTranslation();
  const problems = view.problems ?? [];
  const [choices, setChoices] = useState<Record<string, string>>(() =>
    Object.fromEntries(problems.map((p) => [p.key, view.choices?.[p.key] ?? SKIP])),
  );
  const [saving, setSaving] = useState(false);
  const total = view.total_rows ?? 0;
  const problemRows = problems.reduce((n, p) => n + p.row_numbers.length, 0);

  return (
    <Stack gap="md">
      <Text fw={600}>
        {t("reviews.correct.check.summary", { fine: total - problemRows, total, count: problems.length })}
      </Text>
      <Table data-testid="problem-rows" verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 70 }}>{t("reviews.correct.check.row")}</Table.Th>
            <Table.Th style={{ width: 220 }}>{t("reviews.correct.check.idInFile", { column: view.id_column })}</Table.Th>
            <Table.Th>{t("reviews.correct.check.found")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {problems.map((p) => (
            <Table.Tr key={p.key} data-problem={p.problem}>
              <Table.Td>{p.row_numbers.join(", ")}</Table.Td>
              <Table.Td style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{p.raw_id || "—"}</Table.Td>
              <Table.Td>
                <ProblemChoice
                  problem={p}
                  value={choices[p.key]}
                  onChange={(v) => setChoices((c) => ({ ...c, [p.key]: v }))}
                />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Group justify="space-between">
        <Button variant="subtle" onClick={onStartOver}>{t("reviews.correct.startOver")}</Button>
        <Button
          loading={saving}
          onClick={async () => {
            setSaving(true);
            await onContinue(choices);
            setSaving(false);
          }}
        >
          {t("reviews.correct.check.continue")}
        </Button>
      </Group>
    </Stack>
  );
};

const ProblemChoice: React.FC<{
  problem: ProblemEntry;
  value: string;
  onChange: (v: string) => void;
}> = ({ problem, value, onChange }) => {
  const { t } = useTranslation();
  const options: { value: string; label: string }[] = [];
  if (problem.problem === "ambiguous") {
    for (const c of problem.candidates) {
      const ga = c.display.find((d) => d.header === "GA")?.ui;
      options.push({
        value: String(c.content_id),
        label: `"${firstText(c).slice(0, 60)}"${ga ? ` · GA ${ga}` : ""} · ${verdictLabel(t, c.current)}`,
      });
    }
  }
  if (problem.problem === "conflicting_verdicts") {
    for (const c of problem.conflicting) {
      options.push({
        value: `row:${c.row_number}`,
        label: t("reviews.correct.check.takeRow", { row: c.row_number, verdict: verdictLabel(t, c.verdict) }),
      });
    }
  }
  options.push({ value: SKIP, label: t("reviews.correct.check.skip") });

  return (
    <Stack gap={6}>
      <Text size="sm">
        {t(`reviews.correct.problem.${problem.problem}`, {
          count: problem.candidates.length,
          rows: problem.row_numbers.join(", "),
        })}
      </Text>
      <Radio.Group value={value} onChange={onChange}>
        <Stack gap={4}>
          {options.map((o) => (
            <Radio key={o.value} value={o.value} label={o.label} size="sm" />
          ))}
        </Stack>
      </Radio.Group>
    </Stack>
  );
};

// ── ③ Review ────────────────────────────────────────────────────────────────

const ContentIdCell: React.FC<{ row: ResultRow; length: number }> = ({ row, length }) => {
  const { t } = useTranslation();
  return (
    <Table.Td style={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
      {row.content_hash_id.slice(0, length)}
      {row.chosen && (
        <Text size="xs" c="dimmed" ff="sans-serif">
          {t("reviews.correct.review.chosen", { rows: row.row_numbers.join(", ") })}
        </Text>
      )}
    </Table.Td>
  );
};

const DisplayCells: React.FC<{ row: ResultRow }> = ({ row }) => (
  <>
    {row.display.map((d) => (
      <Table.Td key={d.header}>
        <Text size="sm" lineClamp={2} title={d.ui}>
          {d.ui}
        </Text>
      </Table.Td>
    ))}
  </>
);

const ReviewStep: React.FC<{
  view: CorrectionView;
  hasProblems: boolean;
  onBack: () => void;
  onError: (m: string) => void;
  onDecided: () => void;
}> = ({ view, hasProblems, onBack, onError, onDecided }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const [acting, setActing] = useState(false);
  const rows = useMemo(() => view.will_change ?? [], [view.will_change]);
  const summary = view.summary!;
  const columns = view.columns ?? [];
  const considered = summary.change + summary.add + summary.unchanged;
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const len = view.content_id_length;

  const act = async (fn: () => Promise<unknown>) => {
    setActing(true);
    try {
      await fn();
      onDecided();
    } catch (e) {
      onError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setActing(false);
      setConfirming(false);
    }
  };

  return (
    <Stack gap="lg">
      <Box>
        <Title order={4} mb="xs">
          {t("reviews.correct.review.willChange", { count: summary.to_write, total: considered, percent: summary.percent })}
        </Title>
        {rows.length === 0 ? (
          <Text c="dimmed" size="sm">{t("reviews.correct.review.nothing")}</Text>
        ) : (
          <>
            <Table striped data-testid="will-change" style={{ tableLayout: "fixed", width: "100%" }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 200 }}>ContentId</Table.Th>
                  <Table.Th style={{ width: 100 }}>{t("reviews.correct.review.old")}</Table.Th>
                  <Table.Th style={{ width: 120 }}>{t("reviews.correct.review.new")}</Table.Th>
                  {columns.map((c) => (
                    <Table.Th key={c}>{c}</Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pageRows.map((r) => (
                  <Table.Tr key={r.key} data-new={r.new}>
                    <ContentIdCell row={r} length={len} />
                    <Table.Td>{verdictLabel(t, r.old)}</Table.Td>
                    <Table.Td>{verdictLabel(t, r.new)}</Table.Td>
                    <DisplayCells row={r} />
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Group justify="space-between" mt="xs">
              <Text size="xs" c="dimmed">
                {t("reviews.correct.review.showing", {
                  from: (page - 1) * PAGE_SIZE + 1,
                  to: Math.min(page * PAGE_SIZE, rows.length),
                  total: rows.length,
                })}
              </Text>
              {rows.length > PAGE_SIZE && (
                <Pagination size="sm" value={page} onChange={setPage} total={Math.ceil(rows.length / PAGE_SIZE)} />
              )}
            </Group>
          </>
        )}
      </Box>

      {summary.unchanged > 0 && (
        <Box>
          <Title order={5} mb="xs">
            {t("reviews.correct.review.willNotChange", { count: summary.unchanged })}
          </Title>
          <Table striped data-testid="will-not-change" style={{ tableLayout: "fixed", width: "100%" }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 200 }}>ContentId</Table.Th>
                <Table.Th style={{ width: 100 }}>{t("reviews.correct.review.verdict")}</Table.Th>
                {columns.map((c) => (
                  <Table.Th key={c}>{c}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(view.unchanged_head ?? []).map((r) => (
                <Table.Tr key={r.key}>
                  <ContentIdCell row={r} length={len} />
                  <Table.Td>{verdictLabel(t, r.new)}</Table.Td>
                  <DisplayCells row={r} />
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {summary.unchanged > (view.unchanged_head ?? []).length && (
            <Text size="xs" c="dimmed" mt="xs">{t("reviews.correct.review.firstTen")}</Text>
          )}
        </Box>
      )}

      {summary.skipped > 0 && (
        <Group gap="sm">
          <Text size="sm" data-testid="skipped-count">
            {t("reviews.correct.review.skipped", { count: summary.skipped })}
          </Text>
          {hasProblems && (
            <Button size="xs" variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={onBack}>
              {t("reviews.correct.review.backToCheck")}
            </Button>
          )}
        </Group>
      )}

      <Group>
        <OverviewButton kpi={view.kpi} correctionId={view.correction_id} onError={onError} />
        <Text size="xs" c="dimmed">{t("reviews.correct.review.overviewHint")}</Text>
      </Group>

      <Divider />
      {!view.is_owner && (
        <Alert icon={<IconInfoCircle size={16} />} color="gray">{t("reviews.correct.review.notOwner", { who: view.uploaded_by })}</Alert>
      )}
      <Group justify="space-between">
        <Button variant="subtle" color="red" disabled={!view.is_owner} loading={acting && !confirming}
          onClick={() => void act(() => correctionsApi.reject(view.kpi, view.correction_id))}>
          {t("reviews.correct.review.reject")}
        </Button>
        <Stack gap={2} align="flex-end">
          <Button disabled={summary.to_write === 0 || !view.is_owner} onClick={() => setConfirming(true)}>
            {t("reviews.correct.review.approve", { count: summary.to_write })}
          </Button>
          {summary.to_write === 0 && <Text size="xs" c="dimmed">{t("reviews.correct.review.nothing")}</Text>}
        </Stack>
      </Group>

      <Modal opened={confirming} onClose={() => setConfirming(false)} centered
        title={t("reviews.correct.confirm.title", { count: summary.to_write })}>
        <Stack gap="md">
          <Text size="sm">{t("reviews.correct.confirm.body")}</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConfirming(false)}>{t("common.cancel")}</Button>
            <Button loading={acting}
              onClick={() => void act(() => correctionsApi.approve(view.kpi, view.correction_id))}>
              {t("reviews.correct.confirm.yes", { count: summary.to_write })}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

// ── ④ Done ──────────────────────────────────────────────────────────────────

const DoneStep: React.FC<{ view: CorrectionView; onError: (m: string) => void; onStartNew: () => void }> = ({
  view,
  onError,
  onStartNew,
}) => {
  const { t } = useTranslation();
  const a = view.applied;
  return (
    <Stack gap="md" align="flex-start" data-testid="correction-done" data-state={view.state}>
      {view.state === "applying" && (
        <Group gap="sm">
          <Loader size="sm" />
          <Text size="sm">{t("reviews.correct.done.applying")}</Text>
        </Group>
      )}
      {view.state === "applied" && a && (
        <Alert icon={<IconCheck size={16} />} color="green">
          <Text size="sm" fw={600}>
            {t("reviews.correct.done.applied", {
              count: a.written, toCorrect: a.to_correct, toIncorrect: a.to_incorrect, added: a.added,
            })}
          </Text>
          {a.already_set > 0 && (
            <Text size="sm">{t("reviews.correct.done.alreadySet", { count: a.already_set })}</Text>
          )}
          <Text size="sm">{t("reviews.correct.done.nextExport")}</Text>
        </Alert>
      )}
      {view.state === "rejected" && (
        <Alert icon={<IconInfoCircle size={16} />} color="gray">{t("reviews.correct.done.rejected")}</Alert>
      )}
      {view.state === "apply_failed" && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {t("reviews.correct.done.failed", { id: view.correction_id })}
        </Alert>
      )}
      {view.state !== "applying" && (
        <Group>
          {view.state === "applied" && (
            <OverviewButton kpi={view.kpi} correctionId={view.correction_id} onError={onError} />
          )}
          <Button onClick={onStartNew}>{t("reviews.correct.startNew")}</Button>
        </Group>
      )}
    </Stack>
  );
};
